import React, { useState } from "react";
import { ClubEvent, Game, Participant } from "../types";
import { nightIsStale, nightResults } from "../utils/closeEvent";
import { writeNightResults } from "../utils/events";

/**
 * "Laske sijoitukset uudelleen" päättyneelle liigaillalle (docs/ideat.md A3).
 *
 * Liigaillan sijoitukset kirjataan päättämisen hetkellä. Jos tulosta tai
 * pelaajia korjataan sen jälkeen, finalPosition jää vanhaksi eikä
 * liigataulukko muutu. Laatikko näkyy vain kun kannan luvut eivät vastaa
 * otteluita. Käytössä sekä Tapahtumat- että Historia-välilehden kortissa.
 */
export default function RecalcPlacingsBox({ clubId, event, games, participants, lang }: {
  clubId: string;
  event: ClubEvent;
  games: Game[];
  participants: Participant[];
  lang: "fi" | "en";
}) {
  const [busy, setBusy] = useState(false);
  const stale = !!event.seasonId && participants.length > 0 && nightIsStale(games, participants);
  if (!stale) return null;

  async function recalc() {
    if (busy) return;
    if (!window.confirm(lang === "en"
      ? "Recalculate placings and league points from the current results?"
      : "Lasketaanko sijoitukset ja liigapisteet uudelleen nykyisistä tuloksista?")) return;
    setBusy(true);
    const failed = await writeNightResults(clubId, event.id, nightResults(games, participants));
    setBusy(false);
    if (failed.length > 0) {
      window.alert(lang === "en"
        ? `Could not update: ${failed.join(", ")}. Check your connection and try again.`
        : `Ei voitu päivittää: ${failed.join(", ")}. Tarkista yhteys ja yritä uudelleen.`);
    }
  }

  return (
    <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 10, background: "#fffbeb", border: "1px solid #fcd34d" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e" }}>
        ⚠️ {lang === "en" ? "League placings are out of date" : "Liigasijoitukset eivät ole ajan tasalla"}
      </div>
      <div style={{ fontSize: 11, color: "#b45309", marginTop: 3, lineHeight: 1.4 }}>
        {lang === "en"
          ? "Results have changed since the event ended. The league table still uses the old placings."
          : "Tuloksia on muutettu tapahtuman päättymisen jälkeen. Liigataulukko käyttää yhä vanhoja sijoituksia."}
      </div>
      <button onClick={recalc} disabled={busy} style={{
        border: "none", borderRadius: 10, padding: "10px 0", marginTop: 10, width: "100%", fontSize: 12, fontWeight: 700,
        background: "#92400e", color: "#fff", opacity: busy ? 0.6 : 1, cursor: busy ? "wait" : "pointer",
      }}>
        {busy
          ? (lang === "en" ? "⏳ Recalculating..." : "⏳ Lasketaan...")
          : (lang === "en" ? "Recalculate placings" : "Laske sijoitukset uudelleen")}
      </button>
    </div>
  );
}
